import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import emptyAbout from "@/images/empty_about.png";
import emptyAboutView from "@/images/empty_about_view.png";
import {
  editSummary,
  saveSummary,
  RESET_EDITING_SUMMARY,
} from "@/store/profile/profileActions";
import {
  contentPanelSx,
  panelEmptyPromptSx,
  panelEmptySx,
  panelHeadSx,
} from "../styles";

interface ProfileSummaryProps {
  isUsersProfile: boolean;
}

const ProfileSummary = ({ isUsersProfile }: ProfileSummaryProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const summary = useSelector((state: any) =>
    isUsersProfile
      ? state.profile.summary
      : state.browse.browseProfile.get("summary")
  );
  const isEditingSummary = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("isEditingSummary"))
      : false
  );
  const summaryLoaded = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("summaryLoaded"))
      : true
  );
  const isSavingSummary = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("isSavingSummary"))
      : false
  );

  const { register, handleSubmit, reset } = useForm<{ summary: string }>({
    defaultValues: { summary },
  });

  useEffect(() => {
    reset({ summary });
  }, [summary, reset]);

  const edit = () => dispatch(editSummary());
  const close = () => dispatch({ type: RESET_EDITING_SUMMARY });
  const onSubmit = (values: { summary: string }) =>
    dispatch(saveSummary(values.summary));

  // TextField's own ref goes to the root, so register()'s ref has to be handed
  // to the textarea it wraps.
  const { ref: registerRef, ...registerProps } = register("summary");

  const heading = (action?: React.ReactNode) => (
    <Box sx={panelHeadSx}>
      <Typography variant="h2">{t("About")}</Typography>
      {action}
    </Box>
  );

  const editButton = (
    <IconButton
      aria-label={t("About")}
      onClick={edit}
      size="small"
      sx={{ color: "info.50" }}
    >
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (!summaryLoaded) {
    return (
      <Box sx={contentPanelSx}>
        {heading()}
        <Typography variant="body2" sx={{ color: "grey.800" }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (isEditingSummary) {
    return (
      <>
        <Box sx={contentPanelSx}>
          {heading(
            <IconButton
              aria-label={t("About")}
              onClick={close}
              size="small"
              sx={{ color: "grey.600" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <TextField
                {...registerProps}
                inputRef={registerRef}
                multiline
                minRows={5}
                fullWidth
                placeholder={t("ProfilePhrase4")}
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                disableElevation
                disabled={isSavingSummary}
                startIcon={
                  isSavingSummary ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : null
                }
                sx={{ alignSelf: "flex-end" }}
              >
                {t("save")}
              </Button>
            </Stack>
          </Box>
        </Box>
        <Divider />
      </>
    );
  }

  if (summary) {
    return (
      <>
        <Box sx={contentPanelSx}>
          {heading(isUsersProfile ? editButton : undefined)}
          <Typography variant="body1">{summary}</Typography>
        </Box>
        <Divider />
      </>
    );
  }

  const isOwner = isUsersProfile;
  return (
    <>
      <Box sx={contentPanelSx}>
        {heading(isOwner ? editButton : undefined)}
        <Box sx={panelEmptySx}>
          <Box
            component="img"
            src={isOwner ? emptyAbout : emptyAboutView}
            alt={isOwner ? "empty landscape prompt" : "empty character waving hi"}
          />
          <Typography variant="body1" sx={{ ...panelEmptyPromptSx, color: "grey.800" }}>
            {isOwner ? t("ProfilePhrase3") : t("NoAbout")}
          </Typography>
        </Box>
      </Box>
      <Divider />
    </>
  );
};

export default ProfileSummary;
